import Color from 'colorjs.io';
import type { HexOrRgbColorString } from 'theme-utils/types';

const defaultReturnIfLight: HexOrRgbColorString = '#000000';
const defaultReturnIfDark: HexOrRgbColorString = '#FFFFFF';

type DefaultReturnType = HexOrRgbColorString;

type IsColorLightOrDarkOptions<T> = {
  color: HexOrRgbColorString;
  returnIfLightColor?: T | DefaultReturnType;
  returnIfDarkColor?: T | DefaultReturnType;
};

/** TS overload signatures for more specific return type */
export function isColorLightOrDark(props: {
  color: HexOrRgbColorString;
}): DefaultReturnType;
export function isColorLightOrDark<T>(props: {
  color: HexOrRgbColorString;
  returnIfLightColor: T;
  returnIfDarkColor: T;
}): T;

/**
 * Takes a color and determines whether or not the color is lighter or darker based on luminance.
 *
 * If the color is lighter, the `returnIfLightColor` prop value will be returned and if not, the `returnIfDarkColor` prop value will be returned.
 *
 * If return values are not specified, will return '#000000' if the color is lighter/'#ffffff' if the color is darker.
 */
export function isColorLightOrDark<T>({
  color,
  returnIfLightColor = defaultReturnIfLight,
  returnIfDarkColor = defaultReturnIfDark,
}: IsColorLightOrDarkOptions<T>): T | DefaultReturnType {
  const colorObj = new Color(color);
  const luminance = colorObj.lch[0];
  return luminance > 50 ? returnIfLightColor : returnIfDarkColor;
}

export type ContrastScores = {
  AA: boolean;
  AALarge: boolean;
  AAA: boolean;
  AAALarge: boolean;
};

/** https://developer.mozilla.org/en-US/docs/Web/Accessibility/Understanding_WCAG/Perceivable/Color_contrast */
export const WCAG_CONTRAST_SCORES: Record<keyof ContrastScores, number> = {
  AA: 4.5,
  AALarge: 3,
  AAA: 7,
  AAALarge: 4.5,
};

type MeetsContrastGuidelinesProps = {
  color1: HexOrRgbColorString;
  color2: HexOrRgbColorString;
  contrastScore: keyof ContrastScores;
};

export function meetsContrastGuidelines({
  color1,
  color2,
  contrastScore,
}: MeetsContrastGuidelinesProps): boolean {
  const color1Obj = new Color(color1);
  const color2Obj = new Color(color2);
  const contrastRatio = color1Obj.contrast(color2Obj, 'WCAG21');
  return contrastRatio >= WCAG_CONTRAST_SCORES[contrastScore];
}
